"use client"
import { useState, useEffect, useRef, useMemo } from "react"
import { View, StyleSheet, TouchableOpacity, ScrollView } from "react-native"
import { Text, useTheme, Surface, Divider, IconButton, Tooltip } from "react-native-paper"
import { ChevronRight, ChevronLeft, ChevronDown, ChevronUp, FileIcon } from "lucide-react"
import type { TowerWithCoords, SchemaField } from "../../../types/project"
import { TOWER_PROPERTY_GROUPS } from "../../../constants/towerProperties"
import { SPAN_PROPERTY_GROUPS } from "../../../constants/spanProperties"
import { useUnits } from "../../../contexts/UnitsContext"
import convert from "convert-units"
import { getMeasureType } from "../../../utils/unitUtils"
import { debugLog } from "../../../constants/debug"

interface PropertiesPaneProps {
  isCollapsed: boolean
  onToggle: () => void
  hoveredTower: TowerWithCoords | null
  hoveredSpan: {
    id: string
    name: string
    startTower: TowerWithCoords
    endTower: TowerWithCoords
    properties: Record<string, any>
  } | null
  projectData?: any
}

export function PropertiesPane({ isCollapsed, onToggle, hoveredTower, hoveredSpan, projectData }: PropertiesPaneProps) {
  const theme = useTheme()
  const { activeUnits } = useUnits()
  const [selectedTower, setSelectedTower] = useState<TowerWithCoords | null>(null)
  const [selectedSpan, setSelectedSpan] = useState<{
    id: string
    name: string
    startTower: TowerWithCoords
    endTower: TowerWithCoords
    properties: Record<string, any>
  } | null>(null)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({})
  const scrollViewRef = useRef<ScrollView>(null)

  // Get schemas from project data
  const towerSchema = useMemo(() => projectData?.schemas?.Tower || {}, [projectData])
  const spanSchema = useMemo(() => projectData?.schemas?.Span || {}, [projectData])

  // Helper function to get the property key from the display name
  const getPropertyKeyForDisplayName = (displayName: string): string | undefined => {
    for (const key in spanSchema) {
      if (spanSchema[key].displayName === displayName) {
        return key
      }
    }
    return undefined
  }

  // Helper function to map schema key to property key (remove special characters and lowercase)
  const mapSchemaKeyToPropertyKey = (schemaKey: string): string => {
    return schemaKey
      .replace(/[@&?!]/g, "")
      .trim()
      .toLowerCase()
  }

  // Initialize all groups as expanded
  useEffect(() => {
    const initialExpandedState: Record<string, boolean> = {}

    // Initialize tower property groups
    Object.keys(TOWER_PROPERTY_GROUPS).forEach((group) => {
      initialExpandedState[group] = true
    })

    // Initialize span property groups
    Object.keys(SPAN_PROPERTY_GROUPS).forEach((group) => {
      initialExpandedState[group] = true
    })

    // Always ensure Crossings & Terrain is expanded by default
    initialExpandedState["Crossings & Terrain"] = true
    initialExpandedState["Crossings"] = true

    setExpandedGroups(initialExpandedState)
  }, [])

  // Update selected tower/span when hovered
  useEffect(() => {
    if (hoveredTower) {
      debugLog("Hover detected on tower", hoveredTower.name)
      setSelectedTower(hoveredTower)
      setSelectedSpan(null)
    }

    if (hoveredSpan) {
      debugLog("Hover detected on span", hoveredSpan.name)
      setSelectedSpan(hoveredSpan)
      setSelectedTower(null)
    }
  }, [hoveredTower, hoveredSpan])

  // Handle tower click - persist selection
  const handleTowerClick = (tower: TowerWithCoords) => {
    debugLog("Tower clicked in properties pane", tower.name)
    setSelectedTower(tower)
    setSelectedSpan(null)
    // Scroll to top when selecting a new item
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: 0, animated: true })
    }
  }

  // Handle span click - persist selection
  const handleSpanClick = (span: {
    id: string
    name: string
    startTower: TowerWithCoords
    endTower: TowerWithCoords
    properties: Record<string, any>
  }) => {
    debugLog("Span clicked in properties pane", span.name)
    setSelectedSpan(span)
    setSelectedTower(null)
    // Scroll to top when selecting a new item
    if (scrollViewRef.current) {
      scrollViewRef.current.scrollTo({ y: 0, animated: true })
    }
  }

  // Toggle group expansion
  const toggleGroupExpansion = (group: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [group]: !prev[group],
    }))
  }

  // Get property schema information
  const getPropertySchema = (propKey: string, isSpan: boolean): SchemaField | null => {
    const schema = isSpan ? spanSchema : towerSchema
    if (!schema) return null

    const schemaEntries = Object.entries(schema) as [string, SchemaField][]
    const matchingSchema = schemaEntries.find(([key]) => key === propKey)

    return matchingSchema ? matchingSchema[1] : null
  }

  // Format and convert property value for display
  const formatPropertyValue = (
    value: any,
    propKey: string,
    isSpan: boolean,
  ): { displayValue: string; unit: string | null } => {
    if (value === undefined || value === null) return { displayValue: "N/A", unit: null }
    if (typeof value === "boolean") return { displayValue: value ? "Yes" : "No", unit: null }

    // Get schema information for this property
    const schema = getPropertySchema(propKey, isSpan)
    if (!schema) {
      // If no schema, just return the value as string
      if (typeof value === "number") {
        return {
          displayValue: Number.isInteger(value) ? value.toString() : value.toFixed(2),
          unit: null,
        }
      }
      return { displayValue: value.toString(), unit: null }
    }

    // Get unit information
    const baseUnit = schema.baseUnit
    if (!baseUnit || typeof value !== "number") {
      return {
        displayValue:
          typeof value === "number"
            ? Number.isInteger(value)
              ? value.toString()
              : value.toFixed(2)
            : value.toString(),
        unit: baseUnit || null,
      }
    }

    // Convert value based on active units
    const measureType = getMeasureType(baseUnit)
    if (!measureType || !activeUnits[measureType]) {
      return { displayValue: `${value.toFixed(2)}`, unit: baseUnit }
    }

    try {
      const convertedValue = convert(value).from(baseUnit).to(activeUnits[measureType])
      return {
        displayValue: convertedValue.toFixed(2),
        unit: activeUnits[measureType],
      }
    } catch (error) {
      console.warn(`Could not convert ${value} from ${baseUnit} to ${activeUnits[measureType]}`)
      return { displayValue: value.toFixed(2), unit: baseUnit }
    }
  }

  // Helper function to get property value with fallback
  const getPropertyValue = (properties: Record<string, any>, propertyKey: string) => {
    // First check if the property exists directly
    if (properties[propertyKey] !== undefined) {
      return properties[propertyKey]
    }

    // Try case-insensitive match
    const lowerKey = propertyKey.toLowerCase()
    const matchingKey = Object.keys(properties).find((key) => key.toLowerCase() === lowerKey)

    if (matchingKey) {
      return properties[matchingKey]
    }

    // Try to match by removing special characters
    const normalizedKey = propertyKey.replace(/[@&?!]/g, "").trim()
    const normalizedMatch = Object.keys(properties).find(
      (key) =>
        key
          .replace(/[@&?!]/g, "")
          .trim()
          .toLowerCase() === normalizedKey.toLowerCase(),
    )

    if (normalizedMatch) {
      return properties[normalizedMatch]
    }

    return undefined
  }

  // Render property groups for tower
  const renderTowerPropertyGroups = () => {
    if (!selectedTower) return null

    const tower = selectedTower
    if (!tower) return null

    // Debug: Log tower properties to help diagnose issues
    debugLog("Rendering tower properties for", tower.name)
    debugLog("Tower properties keys", Object.keys(tower.properties))

    return Object.entries(TOWER_PROPERTY_GROUPS).map(([groupName, properties]) => {
      // Filter out properties that don't exist in this tower
      const existingProperties = properties.filter((propKey) => {
        return getPropertyValue(tower.properties, propKey) !== undefined
      })

      if (existingProperties.length === 0) return null

      return (
        <View key={groupName} style={styles.section}>
          <TouchableOpacity style={styles.sectionHeader} onPress={() => toggleGroupExpansion(groupName)}>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{groupName}</Text>
            {expandedGroups[groupName] ? (
              <ChevronUp size={16} color={theme.colors.onSurface} />
            ) : (
              <ChevronDown size={16} color={theme.colors.onSurface} />
            )}
          </TouchableOpacity>

          {expandedGroups[groupName] && (
            <View style={styles.propertyGroup}>
              {existingProperties.map((propKey) => {
                const propValue = getPropertyValue(tower.properties, propKey)
                const { displayValue, unit } = formatPropertyValue(propValue, propKey, false)

                return (
                  <View key={propKey} style={styles.propertyRow}>
                    <Tooltip title={propKey}>
                      <Text style={styles.label}>{propKey}:</Text>
                    </Tooltip>
                    <View style={styles.valueContainer}>
                      <Tooltip title={`${displayValue}${unit ? " " + unit : ""}`}>
                        <Text style={styles.value}>
                          {displayValue}
                          {unit && <Text style={styles.unit}> {unit}</Text>}
                        </Text>
                      </Tooltip>

                      {/* Add PDF icon if there's a PDF path */}
                      {tower.properties[`${propKey}_PDFPath`] && (
                        <TouchableOpacity
                          onPress={() => console.log(`Open PDF: ${tower.properties[`${propKey}_PDFPath`]}`)}
                          style={styles.pdfIcon}
                        >
                          <FileIcon size={14} color={theme.colors.primary} />
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                )
              })}
            </View>
          )}
        </View>
      )
    })
  }

  // Render property groups for span
  const renderSpanPropertyGroups = () => {
    if (!selectedSpan) return null

    const span = selectedSpan
    if (!span) return null

    // Debug: Log span properties for debugging
    debugLog("Rendering span properties for", span.name)
    debugLog("Span properties keys", Object.keys(span.properties))

    return Object.entries(SPAN_PROPERTY_GROUPS).map(([groupName, displayNames]) => {
      // Special handling for Crossings & Terrain group
      if (groupName === "Crossings") {
        return renderCrossingProperties()
      }

      // Filter out properties that don't exist in this span
      const existingProperties = displayNames.filter((displayName) => {
        // Try to find a property that matches this display name
        // First try direct match
        if (span.properties[displayName] !== undefined) {
          return true
        }

        // Try case-insensitive match
        const lowerDisplayName = displayName.toLowerCase()
        const matchingKey = Object.keys(span.properties).find(
          (key) => key.toLowerCase() === lowerDisplayName || key.toLowerCase().includes(lowerDisplayName),
        )

        return matchingKey !== undefined
      })

      if (existingProperties.length === 0) return null

      return (
        <View key={groupName} style={styles.section}>
          <TouchableOpacity style={styles.sectionHeader} onPress={() => toggleGroupExpansion(groupName)}>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{groupName}</Text>
            {expandedGroups[groupName] ? (
              <ChevronUp size={16} color={theme.colors.onSurface} />
            ) : (
              <ChevronDown size={16} color={theme.colors.onSurface} />
            )}
          </TouchableOpacity>

          {expandedGroups[groupName] && (
            <View style={styles.propertyGroup}>
              {existingProperties
                .map((displayName) => {
                  // Try to find a property that matches this display name
                  let propValue
                  let propKey = displayName

                  // First try direct match
                  if (span.properties[displayName] !== undefined) {
                    propValue = span.properties[displayName]
                  } else {
                    // Try case-insensitive match
                    const lowerDisplayName = displayName.toLowerCase()
                    const matchingKey = Object.keys(span.properties).find(
                      (key) => key.toLowerCase() === lowerDisplayName || key.toLowerCase().includes(lowerDisplayName),
                    )

                    if (matchingKey) {
                      propValue = span.properties[matchingKey]
                      propKey = matchingKey
                    }
                  }

                  // If we couldn't find a matching property, skip this one
                  if (propValue === undefined) {
                    return null
                  }

                  // Find the schema entry for this display name
                  const schemaEntries = Object.entries(spanSchema)
                  const matchingSchemaEntry = schemaEntries.find(([_, field]) => field.title === displayName)

                  // Use the schema key for formatting if available
                  const schemaKey = matchingSchemaEntry ? matchingSchemaEntry[0] : propKey
                  const { displayValue, unit } = formatPropertyValue(propValue, schemaKey, true)

                  return (
                    <View key={displayName} style={styles.propertyRow}>
                      <Tooltip title={displayName}>
                        <Text style={styles.label}>{displayName}:</Text>
                      </Tooltip>
                      <View style={styles.valueContainer}>
                        <Tooltip title={`${displayValue}${unit ? " " + unit : ""}`}>
                          <Text style={styles.value}>
                            {displayValue}
                            {unit && <Text style={styles.unit}> {unit}</Text>}
                          </Text>
                        </Tooltip>

                        {/* Add PDF icon if there's a PDF path */}
                        {span.properties[`${propKey}_PDFPath`] && (
                          <TouchableOpacity
                            onPress={() => console.log(`Open PDF: ${span.properties[`${propKey}_PDFPath`]}`)}
                            style={styles.pdfIcon}
                          >
                            <FileIcon size={14} color={theme.colors.primary} />
                          </TouchableOpacity>
                        )}
                      </View>
                    </View>
                  )
                })
                .filter(Boolean)}
            </View>
          )}
        </View>
      )
    })
  }

  // Dedicated function to render crossing properties
  const renderCrossingProperties = () => {
    if (!selectedSpan) return null

    const span = selectedSpan
    if (!span) return null

    // Log all properties to help diagnose crossing properties
    debugLog("Checking for crossing properties in span:", span.name)
    debugLog("All span properties:", Object.keys(span.properties))

    // Find all crossing-related properties
    // First, check for properties defined in SPAN_PROPERTY_GROUPS.Crossings
    const crossingDisplayNames = SPAN_PROPERTY_GROUPS["Crossings"] || []

    // Find matching properties from the predefined list
    const matchingCrossingProps = crossingDisplayNames.filter((displayName) => {
      // Try direct match
      if (span.properties[displayName] !== undefined) {
        return true
      }

      // Try case-insensitive match
      const lowerDisplayName = displayName.toLowerCase()
      return Object.keys(span.properties).some(
        (key) => key.toLowerCase() === lowerDisplayName || key.toLowerCase().includes(lowerDisplayName),
      )
    })

    // If no predefined crossing properties found, look for any property with "crossing" in the name
    const additionalCrossingProps = Object.keys(span.properties).filter((key) => {
      const lowerKey = key.toLowerCase()
      return (
        lowerKey.includes("crossing") ||
        lowerKey.includes("river") ||
        lowerKey.includes("railway") ||
        lowerKey.includes("highway") ||
        lowerKey.includes("valley") ||
        lowerKey.includes("forest") ||
        (lowerKey.startsWith("is") && lowerKey.includes("crossing"))
      )
    })

    // Combine both sets of properties, removing duplicates
    const allCrossingProps = [...new Set([...matchingCrossingProps, ...additionalCrossingProps])]

    debugLog("Found crossing properties:", allCrossingProps)

    if (allCrossingProps.length === 0) {
      // If no crossing properties found, check if there's a crossings array
      if (Array.isArray(span.properties.crossings) && span.properties.crossings.length > 0) {
        return renderSpanCrossings()
      }
      return null
    }

    return (
      <View style={styles.section}>
        <TouchableOpacity style={styles.sectionHeader} onPress={() => toggleGroupExpansion("Crossings & Terrain")}>
          <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>Crossings & Terrain</Text>
          {expandedGroups["Crossings & Terrain"] ? (
            <ChevronUp size={16} color={theme.colors.onSurface} />
          ) : (
            <ChevronDown size={16} color={theme.colors.onSurface} />
          )}
        </TouchableOpacity>

        {expandedGroups["Crossings & Terrain"] && (
          <View style={styles.propertyGroup}>
            {allCrossingProps.map((propKey) => {
              // Get the actual property value
              let propValue = span.properties[propKey]

              // If not found directly, try case-insensitive match
              if (propValue === undefined) {
                const lowerPropKey = propKey.toLowerCase()
                const matchingKey = Object.keys(span.properties).find(
                  (key) => key.toLowerCase() === lowerPropKey || key.toLowerCase().includes(lowerPropKey),
                )

                if (matchingKey) {
                  propValue = span.properties[matchingKey]
                  propKey = matchingKey // Use the actual key for display
                }
              }

              // Format the value for display
              let displayValue = String(propValue)

              // Special handling for boolean values
              if (typeof propValue === "boolean") {
                displayValue = propValue ? "Yes" : "No"
              }

              return (
                <View key={propKey} style={styles.propertyRow}>
                  <Tooltip title={propKey}>
                    <Text style={styles.label}>{propKey}:</Text>
                  </Tooltip>
                  <View style={styles.valueContainer}>
                    <Tooltip title={displayValue}>
                      <Text style={styles.value}>{displayValue}</Text>
                    </Tooltip>
                  </View>
                </View>
              )
            })}
          </View>
        )}
      </View>
    )
  }

  // Handle special case for crossings in span properties
  const renderSpanCrossings = () => {
    if (!selectedSpan) return null

    const span = selectedSpan
    if (!span || !Array.isArray(span.properties.crossings) || span.properties.crossings.length === 0) return null

    return (
      <View style={styles.section}>
        <TouchableOpacity style={styles.sectionHeader} onPress={() => toggleGroupExpansion("Crossings")}>
          <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>Crossings</Text>
          {expandedGroups["Crossings"] ? (
            <ChevronUp size={16} color={theme.colors.onSurface} />
          ) : (
            <ChevronDown size={16} color={theme.colors.onSurface} />
          )}
        </TouchableOpacity>

        {expandedGroups["Crossings"] && (
          <View style={styles.propertyGroup}>
            {span.properties.crossings.map((crossing: any, index: number) => (
              <View key={index} style={styles.propertyRow}>
                <Tooltip title={crossing.type}>
                  <Text style={styles.label}>{crossing.type}:</Text>
                </Tooltip>
                <Tooltip title={crossing.details}>
                  <Text style={styles.value}>{crossing.details}</Text>
                </Tooltip>
              </View>
            ))}
          </View>
        )}
      </View>
    )
  }

  // Render summary data if available
  const renderSummaryData = () => {
    if (!selectedTower) return null

    const tower = selectedTower
    if (!tower || !tower.properties.summary) return null

    const summary = tower.properties.summary

    return Object.entries(summary).map(([groupName, group]) => {
      if (typeof group !== "object" || !group) return null

      return (
        <View key={`summary-${groupName}`} style={styles.section}>
          <TouchableOpacity style={styles.sectionHeader} onPress={() => toggleGroupExpansion(`summary-${groupName}`)}>
            <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>{groupName} (Summary)</Text>
            {expandedGroups[`summary-${groupName}`] ? (
              <ChevronUp size={16} color={theme.colors.onSurface} />
            ) : (
              <ChevronDown size={16} color={theme.colors.onSurface} />
            )}
          </TouchableOpacity>

          {expandedGroups[`summary-${groupName}`] && (
            <View style={styles.propertyGroup}>
              {Object.entries(group).map(([key, value]) => {
                if (typeof value !== "object" || !value) return null

                const rawValue = value.value
                const baseUnit = value.unit || null

                // Convert value if it's a number and has a unit
                let displayValue = rawValue
                let displayUnit = baseUnit

                if (typeof rawValue === "number" && baseUnit) {
                  const measureType = getMeasureType(baseUnit)
                  if (measureType && activeUnits[measureType]) {
                    try {
                      displayValue = convert(rawValue).from(baseUnit).to(activeUnits[measureType])
                      displayUnit = activeUnits[measureType]
                    } catch (error) {
                      console.warn(`Could not convert ${rawValue} from ${baseUnit} to ${activeUnits[measureType]}`)
                      displayValue = rawValue
                    }
                  }
                }

                // Format the display value
                const formattedValue =
                  typeof displayValue === "number"
                    ? Number.isInteger(displayValue)
                      ? displayValue.toString()
                      : displayValue.toFixed(2)
                    : String(displayValue)

                return (
                  <View key={key} style={styles.propertyRow}>
                    <Tooltip title={key}>
                      <Text style={styles.label}>{key}:</Text>
                    </Tooltip>
                    <View style={styles.valueContainer}>
                      <Tooltip title={`${formattedValue}${displayUnit ? " " + displayUnit : ""}`}>
                        <Text style={styles.value}>
                          {formattedValue}
                          {displayUnit && <Text style={styles.unit}> {displayUnit}</Text>}
                        </Text>
                      </Tooltip>

                      {/* Add PDF icon if there's a PDF path */}
                      {value.PDFPath && (
                        <TouchableOpacity
                          onPress={() => console.log(`Open PDF: ${value.PDFPath}`)}
                          style={styles.pdfIcon}
                        >
                          <FileIcon size={14} color={theme.colors.primary} />
                        </TouchableOpacity>
                      )}
                    </View>
                  </View>
                )
              })}
            </View>
          )}
        </View>
      )
    })
  }

  return (
    <Surface
      style={[
        styles.container,
        {
          backgroundColor: theme.colors.elevation.level2,
          width: isCollapsed ? 40 : 320,
        },
      ]}
    >
      <TouchableOpacity style={[styles.toggleButton, { backgroundColor: theme.colors.primary }]} onPress={onToggle}>
        {isCollapsed ? (
          <ChevronLeft size={20} color={theme.colors.onPrimary} />
        ) : (
          <ChevronRight size={20} color={theme.colors.onPrimary} />
        )}
      </TouchableOpacity>

      {!isCollapsed && (
        <View style={styles.content}>
          <View style={styles.header}>
            <Text style={[styles.title, { color: theme.colors.onSurface }]}>
              {selectedTower
                ? `Tower: ${selectedTower.name}`
                : selectedSpan
                  ? `Span: ${selectedSpan.name}`
                  : "Properties"}
            </Text>
            {(selectedTower || selectedSpan) && (
              <IconButton
                icon="close"
                size={20}
                onPress={() => {
                  debugLog("Clearing selected tower/span")
                  setSelectedTower(null)
                  setSelectedSpan(null)
                }}
              />
            )}
          </View>
          <Divider style={styles.divider} />

          <ScrollView style={styles.scrollView} ref={scrollViewRef} showsVerticalScrollIndicator={true}>
            {/* Tower Properties */}
            {selectedTower && (
              <View>
                {renderTowerPropertyGroups()}
                {renderSummaryData()}
              </View>
            )}

            {/* Span Properties */}
            {selectedSpan && <View>{renderSpanPropertyGroups()}</View>}

            {!selectedTower && !selectedSpan && (
              <Text style={[styles.placeholder, { color: theme.colors.onSurfaceVariant }]}>
                Click on a tower or span to view its properties
              </Text>
            )}
          </ScrollView>
        </View>
      )}
    </Surface>
  )
}

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    right: 0,
    height: "100%",
    borderLeftWidth: 1,
    borderLeftColor: "rgba(0,0,0,0.1)",
    transition: "width 0.3s ease",
  },
  toggleButton: {
    position: "absolute",
    left: -20,
    top: "50%",
    transform: [{ translateY: -20 }],
    width: 20,
    height: 40,
    justifyContent: "center",
    alignItems: "center",
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    zIndex: 1,
  },
  content: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 12,
    paddingBottom: 8,
  },
  title: {
    fontSize: 18,
    fontWeight: "500",
  },
  divider: {
    marginBottom: 8,
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: 12,
  },
  entityHeader: {
    paddingVertical: 8,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 8,
  },
  mainTitle: {
    fontSize: 16,
    fontWeight: "500",
  },
  section: {
    marginBottom: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: "rgba(0,0,0,0.03)",
    borderRadius: 4,
  },
  sectionTitle: {
    fontSize: 14,
    fontWeight: "500",
  },
  propertyGroup: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  propertyRow: {
    marginBottom: 4,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  label: {
    fontSize: 12,
    opacity: 0.7,
    marginBottom: 2,
    width: 120,
    paddingRight: 8,
    textAlign: "right",
  },
  valueContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
  },
  value: {
    fontSize: 13,
    fontWeight: "500",
    marginBottom: 6,
    flex: 1,
  },
  unit: {
    fontSize: 12,
    opacity: 0.7,
  },
  pdfIcon: {
    marginLeft: 4,
    padding: 2,
  },
  placeholder: {
    fontSize: 13,
    textAlign: "center",
    opacity: 0.7,
    padding: 16,
  },
})

